"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import { Menu, X, Search, Github, Home } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { WEBSITE_NAME } from "../lib/types";
import { useAuth } from "@/hooks/useAuth";
import { getCurrentSubdomainContext } from "@/app/data/navigation";
import { Button } from "./ui/button";
import { SearchDialog } from "./SearchDialog";
import ThemeToggle from "./ThemeToggle";
import {
  AnimatedNavLink,
  NavMenu,
  UserNav,
  MobileNav,
} from "./NavbarElements";

// Custom hook for scroll detection - optimized to prevent re-renders
function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollDirection = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      const scrollDiff = Math.abs(currentScrollY - lastScrollY);
      
      if (scrollDiff > 5 && direction !== scrollDirection) {
        setScrollDirection(direction);
      }
      
      if (currentScrollY !== scrollY) {
        setScrollY(currentScrollY);
      }
      
      lastScrollY = currentScrollY > 0 ? currentScrollY : 0;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Remove scrollDirection dependency to prevent infinite loops

  return { scrollDirection, scrollY };
}


export function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, refetch, authKey } = useAuth();
  const router = useRouter();
  const subdomainContext = getCurrentSubdomainContext();

  // Scroll-based transforms - temporarily disabled to fix Framer Motion issues
  // const { scrollYProgress } = useScroll();
  // const navbarHeight = useTransform(scrollYProgress, [0, 0.1], [64, 56]);
  // const navbarY = useTransform(
  //   scrollY,
  //   [0, 100],
  //   [0, scrollDirection === 'down' && scrollY > 100 ? -64 : 0]
  // );

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success("Successfully signed out!");

      // Redirect immediately since logout already clears state
      router.push("/");
    } catch (error: any) {
      toast.error(error.message || "Failed to sign out");
    }
  };

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Listen for theme changes - removed filter animation that causes glitches
  useEffect(() => {
    // Theme changes are handled smoothly by CSS transitions
    // No need for JavaScript-based filter animations
  }, []);

  // Listen for openSearch event from homepage hero
  useEffect(() => {
    const handler = () => setSearchOpen(true);
    window.addEventListener("openSearch", handler);
    return () => window.removeEventListener("openSearch", handler);
  }, []);

  return (
    <>
      <motion.header
        key={`navbar-${authKey}`}
        className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-xl shadow-sm h-16 [font-family:var(--font-outfit)]"
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
        }}
        transition={{ type: "spring", stiffness: 200, damping: 25, mass: 0.5 }}
      >
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Left Section: Logo & Main Navigation */}
          <div className="flex items-center gap-6">
            <div>
              <Link
                href={subdomainContext === 'main' ? "/" : "https://eyn.tools"}
                className="flex items-center space-x-2 hover:bg-accent/50 rounded-md px-2 py-1 transition-colors duration-200"
                onClick={closeMobileMenu}
              >
                <span className="font-bold text-lg">
                  {subdomainContext === 'notes' ? 'Notes' :
                   subdomainContext === 'auth' ? 'Authentication' :
                   WEBSITE_NAME}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <AnimatedNavLink href={subdomainContext === 'main' ? "/" : "https://eyn.tools"}>
                <Home className="h-4 w-4" />
                <span>Home</span>
              </AnimatedNavLink>
              <NavMenu />
            </nav>
          </div>

          {/* Right Section: Search, Theme Toggle, Auth, and Mobile Toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              className="hover:bg-accent/50 focus:bg-accent/40 focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-colors duration-200"
            >
              <Search className="h-4 w-4" />
            </Button>

            {/* Theme Toggle */}
            <ThemeToggle />

            <a
              href="https://github.com/WilsonRIP"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex"
            >
              <Button
                variant="ghost"
                size="icon"
                aria-label="GitHub"
                className="hover:bg-accent/50 focus:bg-accent/40 focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-colors duration-200"
              >
                <Github className="h-4 w-4" />
              </Button>
            </a>
            
            <UserNav key={`usernav-${authKey}`} handleSignOutAction={handleSignOut} />

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              className="md:hidden hover:bg-accent/50 focus:bg-accent/40 focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-colors duration-200"
              onClick={toggleMobileMenu}
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={mobileMenuOpen ? "x" : "menu"}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </motion.div>
              </AnimatePresence>
            </Button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <MobileNav
            key={`mobilenav-${authKey}`}
            isOpen={mobileMenuOpen}
            closeMenuAction={closeMobileMenu}
            handleSignOutAction={handleSignOut}
        />
      </motion.header>
      <SearchDialog open={searchOpen} setOpenAction={setSearchOpen} />
    </>
  );
}